// Evidence excerpt operational pattern (DESIGN_SYSTEM.md §10.6).
// Monospace presentation with line numbers, diff highlighting, redacted secret pills,
// expected vs. observed comparison, and provenance/limitations metadata.
// Strictly differentiates raw tool evidence from AI interpretation (FE-042).

import { useState } from 'react';
import { Copy, Check, Eye, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from './Button.tsx';
import { collectorText } from '../features/discovery/collectorLabel.ts';
import styles from './EvidenceExcerpt.module.css';

export interface RedactedSecret {
  token: string;
  label: string;
  unmaskedValue?: string;
}

export interface ModelAttribution {
  provider: string;
  modelId: string;
  modelVersion: string;
  promptTemplateVersion: string;
}

export interface EvidenceExcerptProps {
  evidenceId?: string;
  id?: string;
  kind?: string;
  sourceClass?: 'RAW_TOOL_OUTPUT' | 'DERIVED' | 'MODEL_ASSISTED' | string;
  sourceSystem?: string;
  toolVersion?: string;
  capturedAt?: string;
  content: string;
  contentType?: 'log' | 'http' | 'diff' | 'code';
  expected?: string;
  observed?: string;
  limitations?: string;
  redactions?: RedactedSecret[];
  // Provenance & cryptographic integrity
  sha256?: string;
  integrityState?: string;
  targetContact?: boolean;
  storageRef?: string;
  // Raw vs AI interpretation distinction
  isAiInterpretation?: boolean;
  aiReasoning?: string;
  modelAttribution?: ModelAttribution | null;
  onInspect?: (evidenceId: string) => void;
  onCopy?: () => void;
  /** Inside a pane that already shows the identity and provenance: keep the content and Copy. */
  embedded?: boolean;
}

export function EvidenceExcerpt({
  evidenceId,
  id,
  kind = 'RAW_TOOL_RECORD',
  sourceClass,
  sourceSystem,
  toolVersion,
  capturedAt,
  content,
  embedded = false,
  contentType = 'log',
  expected,
  observed,
  limitations,
  redactions = [],
  sha256,
  targetContact,
  isAiInterpretation = false,
  aiReasoning,
  modelAttribution = null,
  onInspect,
  onCopy,
}: EvidenceExcerptProps) {
  const [copied, setCopied] = useState(false);
  const [unmaskedTokens, setUnmaskedTokens] = useState<Record<string, boolean>>({});
  const resolvedId = evidenceId ?? id ?? 'EV-UNKNOWN';
  const effectiveSourceClass =
    sourceClass || (isAiInterpretation ? 'MODEL_ASSISTED' : 'RAW_TOOL_OUTPUT');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      onCopy?.();
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      setCopied(true);
      onCopy?.();
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleUnmask = (token: string) => {
    setUnmaskedTokens((prev) => ({
      ...prev,
      [token]: !prev[token],
    }));
  };

  const renderContentLine = (line: string, index: number) => {
    const isDiff = contentType === 'diff';
    const isAddition = isDiff && line.startsWith('+') && !line.startsWith('+++');
    const isDeletion = isDiff && line.startsWith('-') && !line.startsWith('---');

    // Check if line contains any redacted secrets
    let renderedLine: React.ReactNode = line;
    if (redactions.length > 0) {
      let segments: React.ReactNode[] = [line];
      for (const red of redactions) {
        const nextSegments: React.ReactNode[] = [];
        for (const seg of segments) {
          if (typeof seg === 'string' && seg.includes(red.token)) {
            const parts = seg.split(red.token);
            parts.forEach((part, i) => {
              if (i > 0) {
                const isUnmasked = !!unmaskedTokens[red.token] && !!red.unmaskedValue;
                nextSegments.push(
                  <span
                    key={`${red.token}-${i}`}
                    className={styles.redactedPill}
                    data-unmasked={isUnmasked ? 'true' : undefined}
                  >
                    <span className={styles.redactedLabel}>
                      {isUnmasked ? red.unmaskedValue : `[REDACTED: ${red.label}]`}
                    </span>
                    {red.unmaskedValue && (
                      <button
                        type="button"
                        className={styles.unmaskBtn}
                        onClick={() => toggleUnmask(red.token)}
                        aria-label={isUnmasked ? 'Mask secret' : 'Unmask secret'}
                      >
                        {isUnmasked ? 'mask' : 'reveal'}
                      </button>
                    )}
                  </span>,
                );
              }
              if (part) nextSegments.push(part);
            });
          } else {
            nextSegments.push(seg);
          }
        }
        segments = nextSegments;
      }
      renderedLine = segments;
    }

    return (
      <div
        key={index}
        className={[
          styles.lineRow,
          isAddition ? styles.addition : '',
          isDeletion ? styles.deletion : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.lineNumber} aria-hidden="true">
          {index + 1}
        </span>
        <span className={styles.lineContent}>{renderedLine}</span>
      </div>
    );
  };

  const lines = content.split('\n');

  return (
    <article
      className={[
        styles.container,
        isAiInterpretation ? styles.aiContainer : styles.rawContainer,
      ].join(' ')}
      aria-label={`Evidence excerpt ${resolvedId}`}
      data-source-class={effectiveSourceClass}
    >
      {/* Header */}
      <header className={styles.header}>
        {!embedded && (
          <div className={styles.meta}>
            <code className={styles.evidenceId}>{resolvedId}</code>
            <span className={styles.kindBadge}>{kind}</span>
            {isAiInterpretation ? (
              <span className={styles.aiBadge}>
                <Sparkles size={12} aria-hidden="true" />
                <span>AI interpretation</span>
              </span>
            ) : (
              <span className={styles.rawBadge}>
                <ShieldCheck size={12} aria-hidden="true" />
                <span>Raw tool output</span>
              </span>
            )}
            {(sourceSystem || toolVersion) && (
              <span className={styles.sourceText}>{collectorText(sourceSystem, toolVersion)}</span>
            )}
            {capturedAt && (
              <time className={styles.timestamp} dateTime={capturedAt} title={capturedAt}>
                {capturedAt}
              </time>
            )}
            {sha256 && (
              <span className={styles.hashChip} title={`Cryptographic hash SHA-256: ${sha256}`}>
                sha256:{sha256.slice(0, 8)}…
              </span>
            )}
            {targetContact === false && (
              <span
                className={styles.passiveChip}
                title="Passive observation only; customer target systems were not directly contacted."
              >
                targetContact: false
              </span>
            )}
          </div>
        )}
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="small"
            onPress={handleCopy}
            aria-label={`Copy excerpt ${resolvedId}`}
          >
            {copied ? (
              <>
                <Check size={14} aria-hidden="true" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <Copy size={14} aria-hidden="true" />
                <span>Copy</span>
              </>
            )}
          </Button>
          {onInspect && (
            <Button
              variant="secondary"
              size="small"
              onPress={() => onInspect(resolvedId)}
              aria-label={`Inspect artifact ${resolvedId}`}
            >
              <Eye size={14} aria-hidden="true" />
              <span>Inspect</span>
            </Button>
          )}
        </div>
      </header>

      {/* AI Interpretation Notice if applicable */}
      {isAiInterpretation && (
        <div className={styles.aiCallout} role="note" aria-label="AI interpretation notice">
          <div className={styles.aiCalloutHeader}>
            <Sparkles size={14} aria-hidden="true" />
            <strong className={styles.aiCalloutTitle}>AI interpretation</strong>
          </div>
          <p className={styles.aiCalloutText}>
            Synthesized from underlying observation data. There are no model calls yet in this
            environment; generated conclusions remain unverified and do not constitute raw ground
            truth.
          </p>
          {aiReasoning && <p className={styles.aiReasoning}>{aiReasoning}</p>}
          {modelAttribution && (
            <div className={styles.modelMeta}>
              <span>
                Model: {modelAttribution.provider} {modelAttribution.modelId} (v
                {modelAttribution.modelVersion})
              </span>
              <span>Prompt: v{modelAttribution.promptTemplateVersion}</span>
            </div>
          )}
        </div>
      )}

      {/* Expected vs Observed (if provided) */}
      {(expected || observed) && (
        <div className={styles.comparisonGrid}>
          {expected && (
            <div className={styles.comparisonCard}>
              <span className={styles.comparisonLabel}>Expected behavior</span>
              <p className={styles.comparisonText}>{expected}</p>
            </div>
          )}
          {observed && (
            <div className={styles.comparisonCard}>
              <span className={styles.comparisonLabel}>Observed behavior</span>
              <p className={styles.comparisonText}>{observed}</p>
            </div>
          )}
        </div>
      )}

      {/* Monospace Code / Log Body */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <pre className={styles.codeBlock} tabIndex={0} aria-label="Evidence artifact content">
        <code>{lines.map((line, idx) => renderContentLine(line, idx))}</code>
      </pre>

      {/* Limitations note */}
      {limitations && (
        <footer className={styles.limitations}>
          <span className={styles.limitationsHeading}>Evidence limitations:</span>{' '}
          <span className={styles.limitationsText}>{limitations}</span>
        </footer>
      )}
    </article>
  );
}
